import React, { useContext, useRef, useState } from 'react'
import { Box, Input, Text, VStack, Image } from '@chakra-ui/react'
import get from 'lodash/get'
import { observer } from 'mobx-react'
import { useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'
import { UploaderIcon } from 'theme/icon'
import { CommonError } from 'types'
import { uploadFile } from 'API/fileUploader'
import Icon from 'components/Icon'
import {
  mockBackPhoto,
  mockFrontPhoto
} from 'components/pages/MyProfilePage/components/BackgroundInformation/components/UploadDriverLicense/constant'
import { FormField, FormLabel, RowWrapper, UploadButton } from 'components/pages/MyProfilePage/myProfilePage.style'
import { IApplicationFormValues } from 'interfaces/application'
import { isHeicFile } from 'utils/common'
import { ApplicationContext } from '../../../../applicationForm.context'
import { DESKTOP_CONTAINER_WIDTH } from '../../../../constant'
import { CompleteScreeningContext } from '../../completeScreening.context'

const DriverLicense = () => {
  const { isDriverLicenseVerified } = useContext(ApplicationContext)
  const { setIsDriverLicenses, isDriverLicenses } = useContext(CompleteScreeningContext)
  const [frontPhoto, setFrontPhoto] = useState<string>(mockFrontPhoto)
  const [backPhoto, setBackPhoto] = useState<string>(mockBackPhoto)
  const [isFrontLoading, setIsFrontLoading] = useState<boolean>(false)
  const [isBackLoading, setIsBackLoading] = useState<boolean>(false)
  const { getValues, reset } = useFormContext()
  const formValue: IApplicationFormValues = getValues()
  const frontFilePickerRef = useRef<HTMLInputElement | null>(null)
  const backFilePickerRef = useRef<HTMLInputElement | null>(null)

  function handleClickUploadFrontPhoto(): void {
    const currentRef = frontFilePickerRef.current || { click: () => {} }
    currentRef.click()
  }

  function handleClickUploadBackPhoto(): void {
    const currentRef = backFilePickerRef.current || { click: () => {} }
    currentRef.click()
  }

  async function handleUploadFile(event: { target: EventTarget }): Promise<string> {
    try {
      const file = get(event, 'target.files.0', '')
      if (isHeicFile(file?.name ?? '')) {
        const heic2any = require('heic2any')
        const jpgFileBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg'
        })

        const jpgFile = new File([jpgFileBlob as BlobPart], `${file.name.replace(/\.heic$/i, '')}.jpg`, {
          type: 'image/jpeg'
        })
        const imageUrl: string = await uploadFile(jpgFile)
        return imageUrl
      }
      const imageUrl: string = await uploadFile(file)
      return imageUrl
    } catch (error) {
      let errorMessage: string = 'Upload image failed.'
      if ((error as CommonError)?.message) {
        errorMessage += ` ${(error as CommonError)?.message}`
      }
      toast.error(errorMessage)
      return ''
    }
  }

  async function handleFrontPhotoChange(event: { target: EventTarget }): Promise<void> {
    try {
      setIsFrontLoading(true)
      const imageUrl: string = await handleUploadFile(event)
      setFrontPhoto(imageUrl)
      reset({ ...formValue, driverLicenseFrontPhotoUrl: imageUrl })
      if (backPhoto && backPhoto !== mockBackPhoto) {
        typeof setIsDriverLicenses === 'function' && setIsDriverLicenses(true)
      }
      toast.success('Upload successfully!')
    } catch (error) {
      let errorMessage: string = 'Upload image failed.'
      if ((error as CommonError)?.message) {
        errorMessage += ` ${(error as CommonError)?.message}`
      }
      toast.error(errorMessage)
    } finally {
      setIsFrontLoading(false)
    }
  }

  async function handleBackPhotoChange(event: { target: EventTarget }): Promise<void> {
    try {
      setIsBackLoading(true)
      const imageUrl: string = await handleUploadFile(event)
      setBackPhoto(imageUrl)
      reset({ ...formValue, driverLicenseBackPhotoUrl: imageUrl })
      if (frontPhoto && frontPhoto !== mockFrontPhoto) {
        typeof setIsDriverLicenses === 'function' && setIsDriverLicenses(true)
      }
      toast.success('Upload successfully!')
    } catch (error) {
      let errorMessage: string = 'Upload image failed.'
      if ((error as CommonError)?.message) {
        errorMessage += ` ${(error as CommonError)?.message}`
      }
      toast.error(errorMessage)
    } finally {
      setIsBackLoading(false)
    }
  }

  return (
    <VStack
      spacing={{ md: 10, lg: isDriverLicenseVerified ? 2 : 12 }}
      marginBottom={{ md: isDriverLicenseVerified ? 9 : 'unset' }}
      marginTop={{ lg: isDriverLicenseVerified ? 2 : 'calc(148px - 24px)' }}
      height="full"
    >
      {isDriverLicenseVerified && backPhoto === mockBackPhoto ? (
        <VStack
          maxWidth={{ base: 'full', md: DESKTOP_CONTAINER_WIDTH }}
          marginX="auto"
          minHeight={{ base: 'full', lg: 'auto' }}
          marginTop={{ base: 8, lg: 40 }}
          spacing={8}
          paddingX={4}
        >
          <Icon iconName="document_submitted.svg" size={64} />
          <Text
            fontWeight="500"
            textAlign="center"
            textColor="gray.700"
            lineHeight={6}
            width="full"
            alignSelf="center"
            marginX={{ base: 4, md: 0 }}
          >
            Your driver license photos were successfully uploaded.
          </Text>
        </VStack>
      ) : (
        <Box width="min(90vw, 500px)" height="min(calc(100vh - 62px - 90px), 600px)">
          <Text
            fontWeight={{ base: 500, lg: 600 }}
            textColor="gray.700"
            width="full"
            alignSelf="center"
            marginTop={6}
            marginBottom={{ base: 4, lg: 6 }}
          >
            Photos of Driver License
          </Text>
          <RowWrapper spacing={{ base: 6, md: 0 }}>
            <FormField spacing={4}>
              <FormLabel>Front Side</FormLabel>
              <Input
                type="file"
                ref={frontFilePickerRef}
                display="none"
                accept=".svg, .png, .jpg, .jpeg"
                onChange={handleFrontPhotoChange}
              />
              <Image
                height={{ base: 'unset', md: '159px' }}
                borderRadius="8px"
                src={formValue?.driverLicenseFrontPhotoUrl ?? frontPhoto}
                alt="front-side"
                width="full"
              />
              <UploadButton
                width="full"
                onClick={handleClickUploadFrontPhoto}
                isLoading={isFrontLoading}
                iconSpacing={2}
                leftIcon={<UploaderIcon />}
              >
                Upload Photos
              </UploadButton>
            </FormField>
            <FormField spacing={4} marginBottom={{ base: '64px !important', md: 'unset' }}>
              <FormLabel>Back Side</FormLabel>
              <Input
                type="file"
                ref={backFilePickerRef}
                display="none"
                accept=".svg, .png, .jpg, .jpeg"
                onChange={handleBackPhotoChange}
              />
              <Image
                height={{ base: 'unset', md: '159px' }}
                borderRadius="8px"
                src={formValue?.driverLicenseBackPhotoUrl ?? backPhoto}
                alt="back-side"
                width="full"
              />
              <UploadButton
                width="full"
                onClick={handleClickUploadBackPhoto}
                isLoading={isBackLoading}
                iconSpacing={2}
                leftIcon={<UploaderIcon />}
              >
                Upload Photos
              </UploadButton>
            </FormField>
          </RowWrapper>
        </Box>
      )}
    </VStack>
  )
}

export default observer(DriverLicense)
